import { SharePanel } from '@/components/FormShare/SharePanel'
import { FormQuickActionsMenu } from '@/components/Forms/FormQuickActionsMenu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { formatRelativeTime } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import type { FormListActionHandlers } from './types'

const statusMap = {
  draft: { label: '草稿', variant: 'secondary' },
  published: { label: '上線', variant: 'success' },
  closed: { label: '已關閉', variant: 'destructive' },
  archived: { label: '已封存', variant: 'secondary' },
} as const

type FormTableProps = FormListActionHandlers & {
  forms: FormUiContract[]
  folders: FormFolderContract[]
  canManageForms: boolean
}

export function FormTable({
  forms,
  folders,
  canManageForms,
  onMoveToFolder,
  onManage,
  onDuplicate,
  onStatusChange,
  onPreview,
  onEdit,
}: FormTableProps) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <span className="sr-only">排序</span>
              </TableHead>
              <TableHead>問卷名稱</TableHead>
              <TableHead>狀態</TableHead>
              <TableHead>回應數</TableHead>
              <TableHead>完成率</TableHead>
              <TableHead>資料夾</TableHead>
              <TableHead>最後修改</TableHead>
              <TableHead>操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {forms.map((form) => (
              <SortableFormTableRow
                key={form.id}
                form={form}
                folders={folders}
                canManageForms={canManageForms}
                onMoveToFolder={onMoveToFolder}
                onManage={onManage}
                onDuplicate={onDuplicate}
                onStatusChange={onStatusChange}
                onPreview={onPreview}
                onEdit={onEdit}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function SortableFormTableRow({
  form,
  folders,
  canManageForms,
  onMoveToFolder,
  onManage,
  onDuplicate,
  onStatusChange,
  onPreview,
  onEdit,
}: FormListActionHandlers & {
  form: FormUiContract
  folders: FormFolderContract[]
  canManageForms: boolean
}) {
  const sortable = useSortable({ id: form.id, disabled: !canManageForms })
  const status = statusMap[form.status]
  const openPrimaryAction = () => {
    if (canManageForms) onEdit(form.id)
    else onManage(form)
  }

  return (
    <tr
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      onClick={openPrimaryAction}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openPrimaryAction()
        }
      }}
      tabIndex={0}
      aria-label={`${canManageForms ? '編輯' : '管理'}問卷：${form.title}`}
      className={`cursor-pointer border-b border-slate-200 transition-colors hover:bg-slate-50 ${
        sortable.isDragging ? 'relative z-10 bg-white shadow-md ring-2 ring-indigo-200' : ''
      }`}
    >
      <TableCell>
        {canManageForms ? (
          <button
            type="button"
            aria-label={`拖移 ${form.title} 排序`}
            className="grid size-7 cursor-grab place-items-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing"
            onClick={(event) => event.stopPropagation()}
            {...sortable.attributes}
            {...sortable.listeners}
          >
            <GripVertical className="size-4" />
          </button>
        ) : (
          <span className="block size-7" />
        )}
      </TableCell>
      <TableCell className="font-medium">{form.title}</TableCell>
      <TableCell>
        <Badge variant={status.variant}>{status.label}</Badge>
      </TableCell>
      <TableCell>{form.responseCount}</TableCell>
      <TableCell>{form.completionRate}%</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {folders
            .filter((folder) => folder.formIds.includes(form.id))
            .map((folder) => (
              <Badge key={folder.id} variant="secondary">
                {folder.name}
              </Badge>
            ))}
        </div>
      </TableCell>
      <TableCell>{formatRelativeTime(form.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {canManageForms && (
            <Button
              variant="ghost"
              size="sm"
              title={`編輯 ${form.title}`}
              onClick={(event) => {
                event.stopPropagation()
                onEdit(form.id)
              }}
            >
              編輯
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            title={`管理 ${form.title}`}
            onClick={(event) => {
              event.stopPropagation()
              onManage(form)
            }}
          >
            管理
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title={`預覽 ${form.title}`}
            onClick={(event) => {
              event.stopPropagation()
              onPreview(form.id)
            }}
          >
            預覽
          </Button>
          <div onClickCapture={(event) => event.stopPropagation()}>
            <SharePanel form={form} />
          </div>
          <FormQuickActionsMenu
            form={form}
            folders={folders}
            canManageForms={canManageForms}
            onMoveToFolder={onMoveToFolder}
            onManage={onManage}
            onDuplicate={onDuplicate}
            onStatusChange={onStatusChange}
            onPreview={onPreview}
            onEdit={onEdit}
          />
        </div>
      </TableCell>
    </tr>
  )
}
